import React from 'react';
import PropTypes from 'prop-types';
import { pdf } from '@react-pdf/renderer';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

// Define styles for PDF
const styles = StyleSheet.create({
  page: {
    padding: 50,
    fontSize: 12,
    fontFamily: 'Helvetica',
  },
  header: {
    marginBottom: 30,
    textAlign: 'center',
  },
  title: {
    fontSize: 22,
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#004AAC',
  },
  subtitle: {
    fontSize: 14,
    color: '#555',
    marginBottom: 20,
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 16,
    marginBottom: 8,
    fontWeight: 'bold',
    color: '#004aac',
  },
  text: {
    fontSize: 12,
    lineHeight: 1.5,
    color: '#1f2937',
  },
  grid: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 10,
  },
  gridItem: {
    width: '50%',
    paddingRight: 10,
  },
  label: {
    fontWeight: 'bold',
    marginBottom: 4,
  },
  disclaimer: {
    fontStyle: 'italic',
    fontSize: 10,
    marginTop: 10,
    textAlign: 'center',
  },
  footer: {
    marginTop: 20,
    textAlign: 'center',
    fontSize: 10,
    color: '#999',
  },
});

// Create PDF Document component
const PatientEducationPDF = ({ patientInfo }) => {
  const sections = {};
  patientInfo?.Sections?.forEach((section) => {
    sections[section.Title] = section.Text;
  });

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.title}>Patient Education Information</Text>
        </View>

        {/* Disclaimer */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Disclaimer</Text>
          <Text style={styles.text}>
            {sections['Disclaimer'] ?? 'Information not provided in patient materials'}
          </Text>
        </View>

        {/* Drug Information */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Drug Information</Text>
          <View style={styles.grid}>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Title</Text>
              <Text style={styles.text}>
                {sections['Title'] ?? 'Information not provided in patient materials'}
              </Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Phonetic Pronunciation</Text>
              <Text style={styles.text}>
                {sections['Phonetic Pronunciation'] ??
                  'Information not provided in patient materials'}
              </Text>
            </View>
          </View>
          <View style={styles.grid}>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Common Brand Name</Text>
              <Text style={styles.text}>
                {sections['Common Brand Name'] ?? 'Information not provided in patient materials'}
              </Text>
            </View>
          </View>
        </View>

        {/* Additional Sections */}
        {[
          'Warning',
          'Uses',
          'How to Use',
          'Side Effects',
          'Precautions',
          'Drug Interactions',
          'Overdose',
          'Missed Dose',
          'Storage',
          'Notes',
        ].map((sectionName) => (
          <View key={sectionName} style={styles.section}>
            <Text style={styles.sectionTitle}>{sectionName}</Text>
            <Text style={styles.text}>
              {sections[sectionName] ?? 'Information not provided in patient materials'}
            </Text>
          </View>
        ))}
      </Page>
    </Document>
  );
};

// Function to generate and download PDF
export const generatePatientEducationPDF = async (patientInfo) => {
  try {
    const blob = await pdf(
      <PatientEducationPDF patientInfo={patientInfo} />,
    ).toBlob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Patient-Education-${Date.now()}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

PatientEducationPDF.propTypes = {
  patientInfo: PropTypes.object.isRequired,
};

export default PatientEducationPDF;
